import type { SubscriptionPlan } from '@unriddle/db/types';
import { differenceInSeconds, endOfDay, endOfMonth } from 'date-fns';
import { P, match } from 'ts-pattern';

export const getPageLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 120)
    .with('BASIC', () => 120)
    .with(
      P.union('PRO', 'UNLIMITED', 'PREMIUM', 'TEAM', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getFastImportLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 5)
    .with('BASIC', () => 5)
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getHighResImportLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 1)
    .with('BASIC', () => 1)
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getImportLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => getFastImportLimit(plan))
    .with('BASIC', () => getFastImportLimit(plan))
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getWordLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 1000)
    .with('BASIC', () => 1000)
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getRecordingLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 1)
    .with('BASIC', () => 1)
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getDailyPageLimit = (plan: SubscriptionPlan) =>
  match(plan)
    .with('FREE', () => 600)
    .with('BASIC', () => 600)
    .with(
      P.union('PREMIUM', 'TEAM', 'PRO', 'UNLIMITED', 'FRIEND'),
      () => Number.POSITIVE_INFINITY,
    )
    .exhaustive();

export const getFastImportCountKey = (userId: string) =>
  `user:${userId}:fastImport`;
export const getHighResImportCountKey = (userId: string) =>
  `user:${userId}:highResImport`;
export const getGenerationCountKey = (userId: string) =>
  `user:${userId}:wordGeneration`;
export const getWordCountKey = (userId: string) =>
  `user:${userId}:dailyWordCount`;
export const getRecordingCountKey = (userId: string) =>
  `user:${userId}:recording`;
export const getImportCountKey = (userId: string) =>
  `user:${userId}:basicImport`;
export const getDailyPageCountKey = (userId: string) =>
  `user:${userId}:dailyPageCount`;

export const getEndOfMonthDuration = () => {
  const now = new Date();
  const endOfCurrentMonth = endOfMonth(now);

  // Calculate the difference in seconds between now and the end of the month
  const expiryTime = differenceInSeconds(endOfCurrentMonth, now);
  return expiryTime;
};

export const getEndOfDayDuration = () => {
  const now = new Date();
  const endOfCurrentDay = endOfDay(now);

  // Calculate the difference in seconds between now and the end of the day
  const expiryTime = differenceInSeconds(endOfCurrentDay, now);
  return expiryTime;
};

export const getPublicChatLimitKey = (ip: string, chatbotId: string) =>
  `publicChat:${ip}:chatbot:${chatbotId}:messageCount`;

export const getPublicChatMessagesKey = (ip: string, chatbotId: string) =>
  `publicChat:${ip}:chatbot:${chatbotId}:messages`;

export const getPublicChatLimit = () => 3;

export const getPublicImportLimitKey = (ip: string) =>
  `publicImport:${ip}:importCount`;

export const getPublicImportLimit = () => 5;
